import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const PredefinedAlertSchema = z
    .object({
        id: zIdSchema,
        name: z.string().trim(),
    })
    .brand<"PredefinedAlert">();

export type PredefinedAlertInputProps = z.input<typeof PredefinedAlertSchema>;
export type PredefinedAlertProps = z.output<typeof PredefinedAlertSchema>;

export class PredefinedAlert {
    private constructor(
        private readonly props: Readonly<PredefinedAlertProps>,
    ) {}

    get id(): Id {
        return this.props.id;
    }

    get name(): string {
        return this.props.name;
    }

    static validate(
        props: Readonly<MaybeValidProps<PredefinedAlertInputProps>>,
    ): Result<PredefinedAlertProps, z.ZodError<PredefinedAlertProps>> {
        const result = PredefinedAlertSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<PredefinedAlertInputProps>>,
    ): Result<PredefinedAlertProps, PartialValid<typeof PredefinedAlert>> {
        const result = partialSafeParse(PredefinedAlertSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<PredefinedAlertInputProps>,
    ): Result<PredefinedAlert, z.ZodError<PredefinedAlertProps>> {
        return PredefinedAlert.validate(props).map(
            (validatedProps) => new PredefinedAlert(validatedProps),
        );
    }

    static createWithRecovery(
        props: PredefinedAlertInputProps,
    ): Result<PredefinedAlert, ModelError<typeof PredefinedAlert>> {
        return PredefinedAlert.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<PredefinedAlertProps> = {
                    id: error.validProps.id,
                    name: "",
                };

                const recoveryProps: Unbrand<PredefinedAlertProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof PredefinedAlert>(
                    new PredefinedAlert(recoveryProps as PredefinedAlertProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new PredefinedAlert(validatedProps));
    }

    toCreateProps(): PredefinedAlertInputProps {
        return {
            id: this.props.id.toCreateProps(),
            name: this.props.name,
        };
    }
}

const PredefinedAlertsSchema = z
    .object({
        predefinedAlerts: z.array(
            z.custom<PredefinedAlert>((val) => val instanceof PredefinedAlert, {
                error: "Invalid PredefinedAlert instance",
            }),
        ),
        count: z.number().min(0),
    })
    .brand<"PredefinedAlerts">();

export type PredefinedAlertsInputProps = z.input<typeof PredefinedAlertsSchema>;
export type PredefinedAlertsProps = z.output<typeof PredefinedAlertsSchema>;

export class PredefinedAlerts {
    private constructor(
        private readonly props: Readonly<PredefinedAlertsProps>,
    ) {}

    get predefinedAlerts(): readonly Readonly<PredefinedAlert>[] {
        return this.props.predefinedAlerts;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): PredefinedAlerts {
        return PredefinedAlerts.create({
            predefinedAlerts: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<PredefinedAlertsInputProps>>,
    ): Result<PredefinedAlertsProps, z.ZodError<PredefinedAlertsProps>> {
        const result = PredefinedAlertsSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<PredefinedAlertsInputProps>,
    ): Result<PredefinedAlerts, z.ZodError<PredefinedAlertsProps>> {
        return PredefinedAlerts.validate(props).map(
            (validatedProps) => new PredefinedAlerts(validatedProps),
        );
    }
}
